import { formatDateToReadable } from '../../date-picker/utilities';
import { getDateStatus } from './utilities';
import { handleEventClick, initCardJoinHandler, initJoinSessionHandler, initSaveLinkHandler, initSendNoteHandler } from './handlers/calendar';
import { templateStore, loadTemplate } from '../../template';
import { getResolvedAppLang } from '../../utils/cookie';

export function renderSessionDetailPopup(sessionData) {
	// Determine status based on date, time and canceled flag
	const dateStatus = getDateStatus(
		sessionData.date || '',
		sessionData.time || '',
		sessionData.canceled || false,
	);

	const session = {
		student: sessionData.student || {},
		status: dateStatus.label,
		topic: sessionData.topic || '',
		date: formatDateToReadable(sessionData.date || ''),
		time: sessionData.time || '',
		format: sessionData.format || '',
		notes: sessionData.notes || '',
	};

	// Update popup content directly using IDs
	const avatarEl = document.getElementById('session-detail-avatar');
	const nameEl = document.getElementById('session-detail-student-name');
	const statusEl = document.getElementById('session-detail-status');
	const topicEl = document.getElementById('session-detail-topic');
	const dateEl = document.getElementById('session-detail-date');
	const timeEl = document.getElementById('session-detail-time');
	const formatEl = document.getElementById('session-detail-format');

	if (avatarEl) avatarEl.src = session.student?.avatar || '';
	if (nameEl) nameEl.textContent = session.student?.name || 'N/A';
	if (topicEl) topicEl.textContent = session.topic || '';
	if (dateEl) dateEl.textContent = session.date || '';
	if (timeEl) timeEl.textContent = session.time || '';
	if (formatEl) formatEl.textContent = session.format || '';

	// Update status badge based on date
	if (statusEl) {
		const statusColors = {
			past: 'bg-success-50 text-success-500',
			upcoming: 'bg-primary-50 text-primary-500',
			canceled: 'bg-error-50 text-error-500',
		};

		const statusKey = dateStatus.key;
		const colorClass = statusColors[statusKey] || 'bg-gray-50 text-gray-500';
		statusEl.innerHTML = `
			<div class="px-2 py-1 ${colorClass} rounded-full">
				<p class="mb-0 text-xs font-medium">${session.status}</p>
			</div>
		`;
	}

	// Update coach notes
	const notesEl = document.getElementById('coach_notes');
	if (notesEl) {
		notesEl.value = sessionData.notes || '';
		// Set readonly if notes already exist
		notesEl.readOnly = !!sessionData.notes;
	}

	// Update link input
	const linkInputEl = document.getElementById('session_link');
	if (linkInputEl) {
		linkInputEl.value = sessionData.join_url || '';
	}
}


export function renderCalendarConfig(eventsData) {
	return {
		locale: getResolvedAppLang(),
		initialView: 'dayGridMonth',
		headerToolbar: {
			left: 'prev today next',
			center: 'title',
			right: 'dayGridMonth,timeGridWeek',
		},
		buttonText: {
			today: wpcore.direct_t({ en: 'Today', fr: 'Aujourd\'hui' }),
			month: wpcore.direct_t({ en: 'Month', fr: 'Mois' }),
			week: wpcore.direct_t({ en: 'Week', fr: 'Semaine' }),
		},
		allDaySlot: false,
		slotMinTime: '07:00:00',
		slotMaxTime: '18:00:00',
		slotDuration: '00:30:00',
		slotLabelInterval: '01:00:00',
		scrollTime: '08:00:00',
		events: eventsData,
		dayMaxEvents: 2,
		eventColor: '#BF7C5A',
		eventDisplay: 'auto',
		displayEventTime: true,
		displayEventEnd: false,
		height: 'auto',
		aspectRatio: 1.8,
		slotMinWidth: 142,

		moreLinkContent: (args) => `+ ${args.num} ${wpcore.direct_t({ en: 'more', fr: 'plus' })}`,

		moreLinkClick: () => {
			const listViewBtn = document.getElementById('view-toggle-list');
			if (listViewBtn) {
				listViewBtn.click();
			}
			return 'none';
		},

		eventClick: handleEventClick,
		eventContent: renderEventContent,
		eventDidMount: applyEventStyles,
	};
}


function renderEventContent(arg) {
	const isTimeGridView = arg.view.type === 'timeGridWeek';
	const student = arg.event.extendedProps.student || 'N/A';
	const duration = arg.event.extendedProps.duration || 0;
	const canceled = arg.event.extendedProps.canceled || false;
	const eventDate = arg.event.start;
	const status = getDateStatus(eventDate, '', canceled);

	const template = templateStore.get('visio/calendar-event.ejs');
	if (!template) {
		return { html: '' };
	}

	// eslint-disable-next-line no-undef
	const html = ejs.render(template, {
		eventDate,
		student,
		duration,
		textColor: status.textColor,
		iconBgColor: status.iconBgColor,
		isTimeGridView,
	});

	return { html: html };
}

function applyEventStyles(info) {
	const canceled = info.event.extendedProps.canceled || false;
	const status = getDateStatus(info.event.start, '', canceled);

	info.el.style.backgroundColor = status.bgColor;
	info.el.style.borderLeft = `3px solid ${status.borderColor}`;
	info.el.style.borderColor = status.borderColor;
	info.el.style.paddingLeft = '4px';
	info.el.style.borderRadius = '4px';

	const allTextElements = info.el.querySelectorAll('div, span');
	allTextElements.forEach((el) => {
		el.style.color = status.textColor;
	});
}

function initViewToggle(calendar) {
	const calendarBtn = document.getElementById('view-toggle-calendar');
	const listBtn = document.getElementById('view-toggle-list');
	const calendarView = document.getElementById('calendar-view');
	const listView = document.getElementById('list-view');

	if (!calendarBtn || !listBtn || !calendarView || !listView) {
		return;
	}

	function switchView(view) {
		if (view === 'calendar') {
			calendarView.classList.remove('hidden');
			listView.classList.add('hidden');
			calendarBtn.classList.add('active');
			listBtn.classList.remove('active');

			// Update calendar size after showing
			setTimeout(() => {
				calendar.updateSize();
			}, 100);
		} else {
			calendarView.classList.add('hidden');
			listView.classList.remove('hidden');
			calendarBtn.classList.remove('active');
			listBtn.classList.add('active');
		}

		localStorage.setItem('visio-view-preference', view);
		// Update URL param without reload
		const url = new URL(window.location);
		url.searchParams.set('view', view);
		window.history.replaceState({}, '', url);
	}

	calendarBtn.addEventListener('click', () => switchView('calendar'));
	listBtn.addEventListener('click', () => switchView('list'));

	// Restore last view from URL param or localStorage
	const urlParams = new URLSearchParams(window.location.search);
	const savedView = urlParams.get('view') || localStorage.getItem('visio-view-preference') || 'calendar';
	switchView(savedView);
}

export const initVisioCalendar = async () => {
	const calendarEl = document.getElementById('visio-calendar');
	if (!calendarEl) return;

	// Load templates
	await Promise.all([
		loadTemplate('visio/calendar-event.ejs'),
		loadTemplate('visio/session-detail-content.ejs'),
	]);

	// Get events data from data attribute
	const eventsDataAttr = calendarEl.getAttribute('data-events');
	const eventsData = eventsDataAttr ? JSON.parse(eventsDataAttr) : [];

	// Check if FullCalendar is loaded
	if (typeof FullCalendar === 'undefined') {
		return;
	}

	// Create and render calendar
	const config = renderCalendarConfig(eventsData);
	// eslint-disable-next-line no-undef
	const calendar = new FullCalendar.Calendar(calendarEl, config);
	calendar.render();

	// Setup view toggle
	initViewToggle(calendar);

	// Setup save link handler
	initSaveLinkHandler();

	// Setup send note handler
	initSendNoteHandler();

	// Setup join session handler
	initJoinSessionHandler();

	// Setup card join handler (for list view cards)
	initCardJoinHandler();
};
